
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {collectLoadedGeometryNodes} from "../LER/LERParser.js";
import {SUPPLY_COLORS} from "../LER/LER.js";

// A generic "digitize your own points/lines/surfaces, with your own named
// attributes" framework. Two kinds of data live here, on purpose kept
// separate (see index.html for where each is persisted):
//
//  - Feature CLASSES (schemas): {name, geometryType, color, attributes[]}.
//    Defined once, reused across every point cloud - stored centrally
//    (/api/feature-classes -> digitizing/feature_classes.json), not tied to
//    any one cloud.
//  - Feature INSTANCES (actual digitized geometry + attribute values):
//    tied to whichever point cloud they were drawn in - stored per cloud
//    (/api/digitizing/<cloud> -> pointclouds/<cloud>/digitizing.json) and
//    reloaded/visualized whenever that same cloud is opened again.
//
// The one behavior specific to a single use case (classifying LAS points
// along a digitized pipe run, per the user's original ask) lives here too,
// as classifyPointsAlongPolyline() - everything else in this module is
// deliberately generic.

export const ATTRIBUTE_TYPES = [
	{value: "text", label: "Tekst"},
	{value: "number", label: "Tal"},
	{value: "boolean", label: "Ja/Nej"},
	{value: "date", label: "Dato"},
	{value: "enum", label: "Liste (fast valg)"},
	{value: "ler_link", label: "LER-reference"},
];

export const GEOMETRY_TYPES = [
	{value: "point", label: "Punkt"},
	{value: "line", label: "Linje"},
	{value: "surface", label: "Flade"},
];

// "Forsyningsart" is a fixed, built-in field on every feature type (not a
// user-defined attribute) - per the user's requirement, classification
// names/colors are keyed by *this*, not by whichever feature type/instance
// happened to draw the points. Reuses LER's own SUPPLY_COLORS palette
// (src/modules/LER/LER.js) so a digitized-and-classified "Termisk" run and
// an LER-sourced "Termisk" line render in the same color language. Codes
// live in ASPRS's user-definable range (64-255) - fixed and never reused
// for anything else, so "Termisk" always means the same code everywhere.
export const SUPPLY_TYPES = [
	{key: "afloeb", label: "Afløb"},
	{key: "el", label: "El"},
	{key: "foeringsroer", label: "Føringsrør"},
	{key: "telekom", label: "Telekom"},
	{key: "termisk", label: "Termisk"},
	{key: "vand", label: "Vand"},
	{key: "andet", label: "Andet/Ukendt"},
];

const SUPPLY_CLASSIFICATION_CODES = {
	afloeb: 64, el: 65, foeringsroer: 66, telekom: 67, termisk: 68, vand: 69, andet: 70,
};

export function supplyClassificationCode(supplyKey){
	return SUPPLY_CLASSIFICATION_CODES[supplyKey] ?? SUPPLY_CLASSIFICATION_CODES.andet;
}

export function supplyLabel(supplyKey){
	let entry = SUPPLY_TYPES.find(s => s.key === supplyKey);
	return entry ? entry.label : "Andet/Ukendt";
}

export function supplyColorHex(supplyKey){
	let color = SUPPLY_COLORS[supplyKey] !== undefined ? SUPPLY_COLORS[supplyKey] : SUPPLY_COLORS.andet;
	return "#" + color.toString(16).padStart(6, "0");
}

// CSV import/export for feature TYPES (the schemas, not digitized
// instances) - lets the class list be bulk-edited in Excel/a text editor
// instead of the one-at-a-time form. One row per attribute (a class with no
// attributes gets one row with the attribute columns blank), class-level
// columns repeated on every row belonging to it - the usual flattened
// one-to-many CSV shape, so it opens/edits/saves cleanly as a spreadsheet.
const CSV_HEADERS = ["Feature-type", "Geometri", "Farve", "Forsyningsart", "Klassificér", "Kildeklasse", "Vækstområde", "Forbindelsesafstand", "Attribut navn", "Attribut type", "Attribut valg"];

const GEOMETRY_LABEL_BY_KEY = {point: "Punkt", line: "Linje", surface: "Flade"};
const GEOMETRY_KEY_BY_LABEL = {Punkt: "point", Linje: "line", Flade: "surface"};

const ATTR_TYPE_LABEL_BY_KEY = {text: "Tekst", number: "Tal", boolean: "Ja/Nej", date: "Dato", enum: "Liste", ler_link: "LER-reference"};
const ATTR_TYPE_KEY_BY_LABEL = Object.fromEntries(Object.entries(ATTR_TYPE_LABEL_BY_KEY).map(([k, v]) => [v, k]));

const SUPPLY_LABEL_BY_KEY = Object.fromEntries(SUPPLY_TYPES.map(s => [s.key, s.label]));
const SUPPLY_KEY_BY_LABEL = Object.fromEntries(SUPPLY_TYPES.map(s => [s.label, s.key]));

// Minimal RFC4180-ish CSV reader/writer - quoted fields, doubled-quote
// escaping, comma/CRLF-safe. Small enough to keep in-house rather than
// pull in a dependency for a handful of rows.
export function parseCsv(text){
	let rows = [];
	let row = [];
	let field = "";
	let inQuotes = false;
	let i = 0;

	while(i < text.length){
		let c = text[i];

		if(inQuotes){
			if(c === '"'){
				if(text[i + 1] === '"'){
					field += '"';
					i += 2;
					continue;
				}
				inQuotes = false;
				i++;
				continue;
			}
			field += c;
			i++;
			continue;
		}

		if(c === '"'){
			inQuotes = true;
			i++;
		}else if(c === ","){
			row.push(field);
			field = "";
			i++;
		}else if(c === "\r"){
			i++;
		}else if(c === "\n"){
			row.push(field);
			rows.push(row);
			row = [];
			field = "";
			i++;
		}else{
			field += c;
			i++;
		}
	}

	if(field !== "" || row.length > 0){
		row.push(field);
		rows.push(row);
	}

	return rows.filter(r => !(r.length === 1 && r[0] === ""));
}

function csvEscape(value){
	let s = value === null || value === undefined ? "" : String(value);
	return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function stringifyCsv(rows){
	return rows.map(row => row.map(csvEscape).join(",")).join("\r\n");
}

// classes -> CSV rows (including the header row).
export function featureClassesToCsvRows(classes){
	let rows = [CSV_HEADERS];

	for(let fc of classes){
		let classify = fc.classify || {};
		let base = [
			fc.name,
			GEOMETRY_LABEL_BY_KEY[fc.geometryType] || fc.geometryType,
			fc.color || "",
			SUPPLY_LABEL_BY_KEY[fc.forsyningsart] || "Andet/Ukendt",
			classify.enabled ? "Ja" : "Nej",
			classify.sourceCode === null || classify.sourceCode === undefined ? "" : classify.sourceCode,
			classify.growthMargin ?? "",
			classify.connectDistance ?? "",
		];

		if(!fc.attributes || fc.attributes.length === 0){
			rows.push([...base, "", "", ""]);
		}else{
			for(let attr of fc.attributes){
				rows.push([...base, attr.label, ATTR_TYPE_LABEL_BY_KEY[attr.type] || attr.type, (attr.options || []).join(";")]);
			}
		}
	}

	return rows;
}

// CSV rows (including header) -> partial feature-class objects: {name,
// geometryType, color, forsyningsart, classify?, attributes: [{label, type,
// options?}]}. Attributes come back without a `key` - the caller assigns
// one the same way the manual form does (slugify the label), so imported
// and hand-typed classes stay consistent.
export function csvRowsToFeatureClasses(rows){
	if(rows.length === 0){
		return [];
	}

	let header = rows[0];
	let col = name => header.indexOf(name);
	let idx = {
		name: col("Feature-type"), geom: col("Geometri"), color: col("Farve"),
		supply: col("Forsyningsart"), classify: col("Klassificér"), source: col("Kildeklasse"),
		margin: col("Vækstområde"), connect: col("Forbindelsesafstand"),
		attrLabel: col("Attribut navn"), attrType: col("Attribut type"), attrOptions: col("Attribut valg"),
	};

	let byName = new Map();
	for(let r = 1; r < rows.length; r++){
		let row = rows[r];
		let name = (row[idx.name] || "").trim();
		if(!name){
			continue;
		}

		if(!byName.has(name)){
			let geometryType = GEOMETRY_KEY_BY_LABEL[row[idx.geom]] || "line";
			let entry = {
				name,
				geometryType,
				color: row[idx.color] || "#e0a020",
				forsyningsart: SUPPLY_KEY_BY_LABEL[row[idx.supply]] || "andet",
				attributes: [],
			};
			if(geometryType === "line"){
				entry.classify = {
					enabled: (row[idx.classify] || "").trim().toLowerCase() === "ja",
					sourceCode: row[idx.source] === "" || row[idx.source] === undefined ? null : parseInt(row[idx.source], 10),
					growthMargin: parseFloat(row[idx.margin]) || 2,
					connectDistance: parseFloat(row[idx.connect]) || 0.15,
				};
			}
			byName.set(name, entry);
		}

		let attrLabel = (row[idx.attrLabel] || "").trim();
		if(attrLabel){
			let attr = {label: attrLabel, type: ATTR_TYPE_KEY_BY_LABEL[row[idx.attrType]] || "text"};
			if(attr.type === "enum"){
				attr.options = (row[idx.attrOptions] || "").split(";").map(s => s.trim()).filter(Boolean);
			}
			byName.get(name).attributes.push(attr);
		}
	}

	return [...byName.values()];
}

// Remaps a digitized instance's attributes when it's reclassified/"promoted"
// to a different feature type (see index.html's own reclassifyDigitizedInstance):
// keeps values for attribute keys the two classes share, drops values whose
// key no longer exists on the new class, and leaves any attribute the new
// class has but the old one didn't simply unset (the user fills it in via
// the usual edit form afterward, same as a freshly-created instance would
// start). `instance.supply` itself is reassigned separately, straight from
// the new class's forsyningsart (same "copied at the moment of
// classification" rule this applies when an instance is first digitized -
// promoting it to a different feature type is a new act of classification).
export function remapAttributesForReclassify(attributes, newFeatureClass){
	let remapped = {};
	for(let attr of newFeatureClass.attributes){
		if(Object.prototype.hasOwnProperty.call(attributes, attr.key)){
			remapped[attr.key] = attributes[attr.key];
		}
	}
	return remapped;
}

export function createId(prefix){
	return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function hexToRGBA(hex, alpha = 1.0){
	let color = new THREE.Color(hex);
	return [color.r, color.g, color.b, alpha];
}

const POINT_MARKER_RADIUS = 0.15;
const LINE_TUBE_RADIUS = 0.06;
const LINE_JOINT_RADIUS = 0.09;
const TUBE_SEGMENTS = 8;
const SURFACE_OPACITY = 0.35;

function buildPointVisual(coords, color){
	let group = new THREE.Object3D();
	let geometry = new THREE.SphereGeometry(POINT_MARKER_RADIUS, 12, 8);
	let material = new THREE.MeshBasicMaterial({color});
	let mesh = new THREE.Mesh(geometry, material);
	mesh.position.set(...coords[0]);
	group.add(mesh);
	return group;
}

function buildLineVisual(coords, color){
	let group = new THREE.Object3D();
	let jointMaterial = new THREE.MeshBasicMaterial({color});

	for(let point of coords){
		let joint = new THREE.Mesh(new THREE.SphereGeometry(LINE_JOINT_RADIUS, 10, 6), jointMaterial);
		joint.position.set(...point);
		group.add(joint);
	}

	let tubeMaterial = new THREE.MeshBasicMaterial({color});
	for(let i = 0; i < coords.length - 1; i++){
		let start = new THREE.Vector3(...coords[i]);
		let end = new THREE.Vector3(...coords[i + 1]);
		let direction = new THREE.Vector3().subVectors(end, start);
		let length = direction.length();
		if(length < 1e-6){
			continue;
		}

		let segment = new THREE.Mesh(
			new THREE.CylinderGeometry(LINE_TUBE_RADIUS, LINE_TUBE_RADIUS, length, TUBE_SEGMENTS),
			tubeMaterial);
		segment.position.addVectors(start, end).multiplyScalar(0.5);
		segment.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
		group.add(segment);
	}

	return group;
}

// Best-fit plane normal for a (roughly) planar point set - Newell's method,
// robust even for a non-convex or slightly noisy polygon.
function estimateNormal(coords){
	let normal = new THREE.Vector3();
	for(let i = 0; i < coords.length; i++){
		let [x1, y1, z1] = coords[i];
		let [x2, y2, z2] = coords[(i + 1) % coords.length];
		normal.x += (y1 - y2) * (z1 + z2);
		normal.y += (z1 - z2) * (x1 + x2);
		normal.z += (x1 - x2) * (y1 + y2);
	}

	if(normal.lengthSq() < 1e-9){
		normal.set(0, 0, 1);
	}

	return normal.normalize();
}

// A digitized "surface" is a rough planar outline (e.g. a damage patch, a
// marked-out area) rather than a surveyed 3D mesh - it's fan/ear-clip
// triangulated on its own best-fit plane and filled in as a flat,
// semi-transparent, double-sided patch.
function buildSurfaceVisual(coords, color){
	if(coords.length < 3){
		return buildLineVisual(coords, color);
	}

	let centroid = new THREE.Vector3();
	for(let [x, y, z] of coords){
		centroid.add(new THREE.Vector3(x, y, z));
	}
	centroid.divideScalar(coords.length);

	let normal = estimateNormal(coords);
	let up = Math.abs(normal.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
	let u = new THREE.Vector3().crossVectors(up, normal).normalize();
	let v = new THREE.Vector3().crossVectors(normal, u).normalize();

	let points2d = coords.map(([x, y, z]) => {
		let p = new THREE.Vector3(x, y, z).sub(centroid);
		return new THREE.Vector2(p.dot(u), p.dot(v));
	});

	let triangles = THREE.ShapeUtils.triangulateShape(points2d, []);

	let positions = [];
	for(let [x, y, z] of coords){
		positions.push(x, y, z);
	}

	let indices = [];
	for(let [a, b, c] of triangles){
		indices.push(a, b, c);
	}

	let geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();

	let material = new THREE.MeshBasicMaterial({
		color, transparent: true, opacity: SURFACE_OPACITY, depthWrite: false, side: THREE.DoubleSide,
	});
	let mesh = new THREE.Mesh(geometry, material);

	let outline = buildLineVisual(coords.concat([coords[0]]), color);

	let group = new THREE.Object3D();
	group.add(mesh, outline);
	return group;
}

// Builds the visual for one feature instance, tagged with userData so
// DigitizingLayer.pickFeatureAt() can recover {instance, featureClass} from
// a raycast hit anywhere in the group.
export function buildFeatureVisual(featureClass, instance){
	let color = new THREE.Color(featureClass.color || "#e0a020");

	let group;
	if(featureClass.geometryType === "point"){
		group = buildPointVisual(instance.coords, color);
	}else if(featureClass.geometryType === "surface"){
		group = buildSurfaceVisual(instance.coords, color);
	}else{
		group = buildLineVisual(instance.coords, color);
	}

	group.traverse(object => {
		object.userData.digitizingInstance = instance;
		object.userData.digitizingFeatureClass = featureClass;
	});

	return group;
}

// One point cloud's worth of digitized feature instances. Positioned by a
// world-space offset (same reasoning as LERLayer: keeps stored/rendered
// coordinates small and float32-safe instead of raw EPSG:25832 magnitudes).
export class DigitizingLayer extends THREE.Object3D{

	constructor(offset){
		super();
		this.name = "digitizing_layer";
		this.position.set(...offset);
		this.entries = new Map(); // id -> {instance, featureClass, object3d}
		this.classVisibility = new Map(); // featureClassId -> bool
	}

	getOffset(){
		return this.position.toArray();
	}

	addInstance(instance, featureClass){
		this.removeInstance(instance.id);

		let object3d = buildFeatureVisual(featureClass, instance);
		this.add(object3d);
		this.entries.set(instance.id, {instance, featureClass, object3d});

		let visible = this.classVisibility.has(featureClass.id) ? this.classVisibility.get(featureClass.id) : true;
		object3d.visible = visible;

		return object3d;
	}

	removeInstance(id){
		let entry = this.entries.get(id);
		if(!entry){
			return;
		}

		this.remove(entry.object3d);
		entry.object3d.traverse(o => {
			if(o.geometry){
				o.geometry.dispose();
			}
			if(o.material){
				o.material.dispose();
			}
		});
		this.entries.delete(id);
	}

	setClassVisible(featureClassId, visible){
		this.classVisibility.set(featureClassId, visible);
		for(let entry of this.entries.values()){
			if(entry.featureClass.id === featureClassId){
				entry.object3d.visible = visible;
			}
		}
	}

	listInstances(){
		return [...this.entries.values()];
	}

	pickFeatureAt(raycaster){
		let candidates = [];
		for(let entry of this.entries.values()){
			if(entry.object3d.visible){
				entry.object3d.traverse(o => { if(o.isMesh) candidates.push(o); });
			}
		}

		let hits = raycaster.intersectObjects(candidates, false);
		if(hits.length === 0){
			return null;
		}

		return {
			instance: hits[0].object.userData.digitizingInstance,
			featureClass: hits[0].object.userData.digitizingFeatureClass,
		};
	}

	// Serializes back to the on-disk shape ({offset, features}) - see
	// index.html's saveDigitizing().
	toJSON(){
		return {
			offset: this.getOffset(),
			features: [...this.entries.values()].map(e => e.instance),
		};
	}

	dispose(){
		if(this.parent){
			this.parent.remove(this);
		}
	}
}

// Sets up (or updates) a classification LUT entry so a newly-assigned
// classification code renders as a distinct, visible color - without this
// an unlisted code would fall back to ClassificationScheme.DEFAULT's dull
// teal for every custom class alike. Mutates the material's shared
// classification scheme object directly (matches how PointCloudMaterial
// itself is written - see recomputeClassification()).
export function ensureClassificationEntry(material, code, hexColor, name){
	material.classification[code] = {
		visible: true,
		name: name || `Klasse ${code}`,
		color: hexToRGBA(hexColor, 1.0),
	};
	material.recomputeClassification();
}

function nodeWorldMatrix(pointcloud, geometryNode){
	let nodeMatrix = new THREE.Matrix4().makeTranslation(...geometryNode.boundingBox.min.toArray());
	return new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);
}

// Reads the classification of whatever currently-loaded octree point sits
// nearest `worldPos` (within `maxSearchRadius`) - used to find out what
// class the user's very first click landed on, so tracing knows which
// existing class the pipe run is *already* classified as (see the doc
// comment on traceClassifiedRun below for why that matters). Returns null
// if nothing classified is within range.
export function findNearestClassification(pointcloud, worldPos, maxSearchRadius = 1.0){
	let target = new THREE.Vector3(...worldPos);
	let best = null;
	let bestDist = maxSearchRadius;
	let p = new THREE.Vector3();

	for(let geometryNode of collectLoadedGeometryNodes(pointcloud.root)){
		let geometry = geometryNode.geometry;
		if(!geometry.attributes.classification){
			continue;
		}

		let numPoints = geometryNode.numPoints;
		let position = geometry.attributes.position.array;
		let classification = geometry.attributes.classification.array;
		let matrix = nodeWorldMatrix(pointcloud, geometryNode);

		for(let i = 0; i < numPoints; i++){
			p.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]).applyMatrix4(matrix);
			let d = p.distanceTo(target);
			if(d < bestDist){
				bestDist = d;
				best = classification[i];
			}
		}
	}

	return best;
}

// Deliberately conservative: the flood fill's neighbor search costs
// roughly O(candidates x average points per connectDistance-sized grid
// cell), which for a source class covering a large fraction of a dense
// scan (a generic "Building"/"installation" code, easily tens of millions
// of points across a whole cloud) can turn "a few hundred thousand
// candidates" into tens of seconds of blocked main-thread JS - previously
// discovered the hard way as an apparent browser hang. Lower growthMargin
// and maxCandidates trade "might need a second, bigger-margin attempt for
// an unusually long run" against "never freezes the tab by default".
const DEFAULT_MAX_CANDIDATES = 50000;

// The actual "point out a pipe run and have it find the rest of it" search.
// Point-and-click digitizing of a whole run by hand doesn't use what the LAS
// file already knows (per the user: "mine las filer indeholder en eksisterende
// classification") - a run is normally *already* classified as some generic
// "installation"/"pipe" code, just not broken out into individual named
// runs. This is a region-growing / paint-bucket style search, not a
// blind stamp along the drawn line:
//
//  1. Reads the classification at the first waypoint (or uses an explicit
//     sourceCode override) - that's the "this is a pipe" signal to search
//     within.
//  2. Gathers candidate points of that same classification from inside a
//     bounding box around the waypoints, padded by growthMargin - purely a
//     performance/safety bound (so a source class covering a huge chunk of
//     the whole cloud, e.g. a generic "Building" code, doesn't force
//     scanning/clustering millions of points everywhere), not a corridor
//     the result is confined to.
//  3. Finds a seed point near each waypoint (within seedRadius) and floods
//     outward through connected same-classification candidates
//     (connectDistance hops) with NO further distance limit from the
//     original line - so a short digitized mark still picks up the pipe's
//     entire real, possibly much longer, extent, stopping only where the
//     scan data itself is actually disconnected from anything else.
//  4. Reclassifies every point reached by that flood fill.
//
// Returns diagnostics (sourceCode found, candidate/touched counts, whether
// the candidate scan hit maxCandidates and was truncated) so the caller can
// tell the user what was actually matched, since "0 points" or "grabbed way
// more than expected" both need to be visible rather than silent.
export function traceClassifiedRun(pointcloud, worldWaypoints, options = {}){
	let {
		growthMargin = 2.0,
		seedRadius = 0.5,
		connectDistance = 0.15,
		sourceCode = null,
		targetCode,
		maxCandidates = DEFAULT_MAX_CANDIDATES,
	} = options;

	let resolvedSourceCode = sourceCode;
	if(resolvedSourceCode === null || resolvedSourceCode === undefined){
		resolvedSourceCode = findNearestClassification(pointcloud, worldWaypoints[0], seedRadius * 3);
	}

	if(resolvedSourceCode === null || resolvedSourceCode === undefined){
		return {touchedPoints: 0, touchedNodes: 0, sourceCode: null, candidatePoints: 0, reason: "no-source-classification"};
	}

	let min = [Infinity, Infinity, Infinity];
	let max = [-Infinity, -Infinity, -Infinity];
	for(let wp of worldWaypoints){
		for(let a = 0; a < 3; a++){
			min[a] = Math.min(min[a], wp[a] - growthMargin);
			max[a] = Math.max(max[a], wp[a] + growthMargin);
		}
	}

	// Nodes are processed nearest-to-the-digitized-points first, so that if
	// maxCandidates is hit (a dense/large source class can hit it almost
	// immediately), the cut gets made at the far edges of the growth area
	// rather than risking the seed's own neighborhood never being scanned
	// at all - which would otherwise report "no connected points nearby"
	// even though the run is sitting right there.
	let centroid = new THREE.Vector3();
	for(let wp of worldWaypoints){
		centroid.add(new THREE.Vector3(...wp));
	}
	centroid.divideScalar(worldWaypoints.length);

	let nodeCenter = new THREE.Vector3();
	let orderedNodes = collectLoadedGeometryNodes(pointcloud.root)
		.map(geometryNode => {
			let matrix = nodeWorldMatrix(pointcloud, geometryNode);
			nodeCenter.copy(geometryNode.boundingBox.getCenter(new THREE.Vector3())).applyMatrix4(matrix);
			return {geometryNode, matrix, distSq: nodeCenter.distanceToSquared(centroid)};
		})
		.sort((a, b) => a.distSq - b.distSq);

	let candidates = []; // {geometryNode, index, pos}
	let p = new THREE.Vector3();
	let truncated = false;

	outer:
	for(let {geometryNode, matrix} of orderedNodes){
		let geometry = geometryNode.geometry;
		if(!geometry.attributes.classification){
			continue;
		}

		let numPoints = geometryNode.numPoints;
		let position = geometry.attributes.position.array;
		let classification = geometry.attributes.classification.array;

		for(let i = 0; i < numPoints; i++){
			if(classification[i] !== resolvedSourceCode){
				continue;
			}

			p.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]).applyMatrix4(matrix);

			if(p.x < min[0] || p.x > max[0] || p.y < min[1] || p.y > max[1] || p.z < min[2] || p.z > max[2]){
				continue;
			}

			candidates.push({geometryNode, index: i, pos: p.clone()});
			if(candidates.length >= maxCandidates){
				truncated = true;
				break outer;
			}
		}
	}

	if(candidates.length === 0){
		return {touchedPoints: 0, touchedNodes: 0, sourceCode: resolvedSourceCode, candidatePoints: 0, reason: "no-candidates"};
	}

	// Spatial grid over the candidates, for both seed lookup and the flood
	// fill's neighbor queries.
	let cellSize = Math.max(connectDistance, 1e-3);
	let cellCoordsOf = (v) => [Math.floor(v.x / cellSize), Math.floor(v.y / cellSize), Math.floor(v.z / cellSize)];
	let grid = new Map();
	for(let idx = 0; idx < candidates.length; idx++){
		let [cx, cy, cz] = cellCoordsOf(candidates[idx].pos);
		let key = `${cx},${cy},${cz}`;
		if(!grid.has(key)){
			grid.set(key, []);
		}
		grid.get(key).push(idx);
	}

	let connectDistanceSq = connectDistance * connectDistance;

	function neighborsOf(idx){
		let pos = candidates[idx].pos;
		let [cx, cy, cz] = cellCoordsOf(pos);
		let result = [];
		for(let dx = -1; dx <= 1; dx++){
			for(let dy = -1; dy <= 1; dy++){
				for(let dz = -1; dz <= 1; dz++){
					let bucket = grid.get(`${cx + dx},${cy + dy},${cz + dz}`);
					if(!bucket){
						continue;
					}
					for(let other of bucket){
						if(pos.distanceToSquared(candidates[other].pos) <= connectDistanceSq){
							result.push(other);
						}
					}
				}
			}
		}
		return result;
	}

	function nearestWithin(target, radius){
		let [cx, cy, cz] = cellCoordsOf(target);
		let span = Math.max(1, Math.ceil(radius / cellSize));
		let best = -1;
		let bestDist = radius;
		for(let dx = -span; dx <= span; dx++){
			for(let dy = -span; dy <= span; dy++){
				for(let dz = -span; dz <= span; dz++){
					let bucket = grid.get(`${cx + dx},${cy + dy},${cz + dz}`);
					if(!bucket){
						continue;
					}
					for(let idx of bucket){
						let d = target.distanceTo(candidates[idx].pos);
						if(d < bestDist){
							bestDist = d;
							best = idx;
						}
					}
				}
			}
		}
		return best;
	}

	let visited = new Set();
	let queue = [];
	for(let wp of worldWaypoints){
		let seed = nearestWithin(new THREE.Vector3(...wp), seedRadius);
		if(seed >= 0 && !visited.has(seed)){
			visited.add(seed);
			queue.push(seed);
		}
	}

	if(queue.length === 0){
		return {touchedPoints: 0, touchedNodes: 0, sourceCode: resolvedSourceCode, candidatePoints: candidates.length, reason: "no-seed-nearby"};
	}

	while(queue.length > 0){
		let cur = queue.pop();
		for(let next of neighborsOf(cur)){
			if(!visited.has(next)){
				visited.add(next);
				queue.push(next);
			}
		}
	}

	let touchedNodes = new Set();
	for(let idx of visited){
		let {geometryNode, index} = candidates[idx];
		geometryNode.geometry.attributes.classification.array[index] = targetCode;
		touchedNodes.add(geometryNode);
	}
	for(let geometryNode of touchedNodes){
		geometryNode.geometry.attributes.classification.needsUpdate = true;
	}

	return {
		touchedPoints: visited.size,
		touchedNodes: touchedNodes.size,
		sourceCode: resolvedSourceCode,
		candidatePoints: candidates.length,
		truncated,
	};
}
